import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { MainNavBar, SideNavBar, NavSection } from 'components/core';
import { RouteConstants } from '../../../constants';
import 'styles/global.css';
import DataPlatformLogo1 from 'assets/images/logo1.jpeg';
import DataPlatformLogo2 from 'assets/images/logo2.jpeg';
import DataPlatformLogo3 from 'assets/images/logo3.jpeg';
import { useRecoilValue } from 'recoil';
import { currentMenu as currentMenuAtom, authState } from 'states';
import { Navigate } from 'react-router-dom';

interface CommonLayoutProps {
    children?: any;
}

// Menu sections depend on the active language, so they're built from `t`
// inside the component (recreated each render) rather than as module-level
// constants, which would freeze in whatever language was active at load time.
// Each item carries both its translation `key` (stored in currentMenuAtom —
// a stable, language-independent identifier) and its already-translated
// `name` (used for the sidebar list itself, which re-renders on every
// language change anyway).
const getDataAnalyticsSection = (t: TFunction): NavSection => ({
    id: 0,
    title: t('NAV.DATA_ANALYTICS'),
    children: [
        { id: 0, key: 'NAV.DASHBOARDS',     name: t('NAV.DASHBOARDS'),     link: [RouteConstants.dashboards],     icon: 'dashboard' },
        { id: 1, key: 'NAV.VISUALIZATIONS', name: t('NAV.VISUALIZATIONS'), link: [RouteConstants.visualizations], icon: 'bar_chart'  },
    ],
});

const getUserManagementSection = (t: TFunction): NavSection => ({
    id: 2,
    title: t('NAV.USER_MANAGEMENT'),
    children: [
        { id: 0, key: 'NAV.USERS', name: t('NAV.USERS'), link: [RouteConstants.users], icon: 'group'    },
        { id: 1, key: 'NAV.ROLES', name: t('NAV.ROLES'), link: [RouteConstants.roles], icon: 'settings' },
    ],
});

export const CommonLayout = ({ children }: CommonLayoutProps) => {
    const { t } = useTranslation();
    // currentMenuAtom stores a translation KEY (e.g. 'NAV.DASHBOARDS'), not
    // display text — SideNavBar sets it on click/mount. Translating it here,
    // at render time, is what makes the navbar heading actually update on a
    // language switch instead of staying frozen in whichever language was
    // active when the menu was last set.
    const menuKey = useRecoilValue(currentMenuAtom);
    const auth = useRecoilValue(authState);
    const selectedMenu = menuKey ? t(menuKey) : t('NAV.DASHBOARDS');
    const dataAnalyticsSection = getDataAnalyticsSection(t);
    const userManagementSection = getUserManagementSection(t);

    const roleCode: string =
        auth?.roles?.[0]?.code ||
        auth?.scopeLevel ||
        JSON.parse(localStorage.getItem('user') || '{}')?.roles?.[0]?.code ||
        '';

    const isSuperAdmin = roleCode === 'SUPER_ADMIN' || auth?.roles?.[0]?.superAdmin === true;
    const isAdmin      = roleCode === 'ROLE_ADMIN'  || auth?.roles?.[0]?.admin      === true;

    let sideNavItemsData = [dataAnalyticsSection, userManagementSection];
    if (!isSuperAdmin && isAdmin) {
        sideNavItemsData = [userManagementSection];
    }

    const currentPath = window.location.pathname;
    if (isAdmin && !isSuperAdmin) {
        const isAnalyticsRoute = dataAnalyticsSection.children
            .flatMap((c) => c.link)
            .some((l) => currentPath.startsWith(l));
        if (isAnalyticsRoute) {
            return <Navigate to={RouteConstants.users} replace />;
        }
    }

    return (
        <div className="vh-100 d-flex flex-column">
            <MainNavBar
                isCollection={false}
                navString={selectedMenu}
                showSearch={false}
                showNotifications={false}
                logo1={DataPlatformLogo1}
                logo2={DataPlatformLogo2}
                logo3={DataPlatformLogo3}
                logoLink={isSuperAdmin ? RouteConstants.dashboards : RouteConstants.users}
                logoutLink={RouteConstants.root}
            />
            <div className="h-100 container-fluid">
                <div className="row h-100">
                    <div className="col-sm-12 col-md-3 col-lg-2 col-xl-2 col-xxl-2 p-0">
                        <SideNavBar navItems={sideNavItemsData} showIcons={true} />
                    </div>
                    <div className="col-sm-12 col-md-9 col-lg-10 col-xl-10 col-xxl-10 p-0 grey_bg">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};